import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { client } from '../../utils/client'
import { OrderStatus } from 'src/utils/enum'

// Actions - used with useDispatch
export const orderSlice = createSlice({
  name: 'order',
  initialState: {
    pendingOrders: null,
    acceptedOrders: null,
    pendingOrdersPagination: null,
    acceptedOrdersPagination: null,
    getPendingOrders: {
      status: null,
      error: null,
    },
    getAcceptedOrders: {
      status: null,
      error: null,
    },
    acceptOrder: {
      status: null,
      error: null,
    },
    checkinOrder: {
      status: null,
      error: null,
    },
    processOrder: {
      status: null,
      error: null,
    },
    readyOrder: {
      status: null,
      error: null,
    },
    checkoutOrder: {
      status: null,
      error: null,
    },
    addTimeline: {
      status: null,
      error: null,
    },
    getTimeline: {
      status: null,
      error: null,
    },
    getReview: {
      status: null,
      error: null,
    },
    modifyOrderItemPrices: {
      status: null,
      error: null,
    },
    requestAdditionalPayment: {
      status: null,
      error: null,
    },
    orderTabCounts: null, // { 2..7: number }
  },
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        // Clear status and error
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getPendingOrders
      .addCase(getPendingOrders.pending, (state, action) => {
        state.getPendingOrders.status = 'loading'
      })
      .addCase(getPendingOrders.fulfilled, (state, action) => {
        state.getPendingOrders.status = 'succeeded'
        state.getPendingOrders.error = null // Clear any previous errors

        // Store pendingOrders info
        if (action.payload.data.success) {
          // Handle paginated response
          if (action.payload.data.data && Array.isArray(action.payload.data.data)) {
            state.pendingOrders = action.payload.data.data
            if (action.payload.data.pagination) {
              state.pendingOrdersPagination = action.payload.data.pagination
            } else {
              state.pendingOrdersPagination = null
            }
          } else {
            state.pendingOrders = action.payload.data.data
            state.pendingOrdersPagination = null
          }
        }
      })
      .addCase(getPendingOrders.rejected, (state, action) => {
        state.getPendingOrders.status = 'failed'
        state.getPendingOrders.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear pendingOrders info
        state.pendingOrders = null
      })
      // getAcceptedOrders
      .addCase(getAcceptedOrders.pending, (state, action) => {
        state.getAcceptedOrders.status = 'loading'
      })
      .addCase(getAcceptedOrders.fulfilled, (state, action) => {
        state.getAcceptedOrders.status = 'succeeded'
        state.getAcceptedOrders.error = null // Clear any previous errors

        // Store acceptedOrders info
        if (action.payload.data.success) {
          // Handle paginated response
          if (action.payload.data.data && Array.isArray(action.payload.data.data)) {
            state.acceptedOrders = action.payload.data.data
            if (action.payload.data.pagination) {
              state.acceptedOrdersPagination = action.payload.data.pagination
            } else {
              state.acceptedOrdersPagination = null
            }
          } else {
            state.acceptedOrders = action.payload.data.data
            state.acceptedOrdersPagination = null
          }
        }
      })
      .addCase(getAcceptedOrders.rejected, (state, action) => {
        state.getAcceptedOrders.status = 'failed'
        state.getAcceptedOrders.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear acceptedOrders info
        state.acceptedOrders = null
      })
      // getOrderTabCounts
      .addCase(getOrderTabCounts.fulfilled, (state, action) => {
        if (action.payload?.tabCounts) {
          state.orderTabCounts = action.payload.tabCounts
        }
      })
      .addCase(getOrderTabCounts.rejected, (state) => {
        state.orderTabCounts = null
      })
      // acceptOrder
      .addCase(acceptOrder.pending, (state, action) => {
        state.acceptOrder.status = 'loading'
      })
      .addCase(acceptOrder.fulfilled, (state, action) => {
        state.acceptOrder.status = 'succeeded'
        state.acceptOrder.error = null // Clear any previous errors

        // Thing to do after accept order, if any
      })
      .addCase(acceptOrder.rejected, (state, action) => {
        state.acceptOrder.status = 'failed'
        state.acceptOrder.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // checkinOrder
      .addCase(checkinOrder.pending, (state, action) => {
        state.checkinOrder.status = 'loading'
      })
      .addCase(checkinOrder.fulfilled, (state, action) => {
        state.checkinOrder.status = 'succeeded'
        state.checkinOrder.error = null // Clear any previous errors

        // Thing to do after checkin order, if any
      })
      .addCase(checkinOrder.rejected, (state, action) => {
        state.checkinOrder.status = 'failed'
        state.checkinOrder.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // processOrder
      .addCase(processOrder.pending, (state, action) => {
        state.processOrder.status = 'loading'
      })
      .addCase(processOrder.fulfilled, (state, action) => {
        state.processOrder.status = 'succeeded'
        state.processOrder.error = null // Clear any previous errors

        // Thing to do after process order, if any
      })
      .addCase(processOrder.rejected, (state, action) => {
        state.processOrder.status = 'failed'
        state.processOrder.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // readyOrder
      .addCase(readyOrder.pending, (state, action) => {
        state.readyOrder.status = 'loading'
      })
      .addCase(readyOrder.fulfilled, (state, action) => {
        state.readyOrder.status = 'succeeded'
        state.readyOrder.error = null // Clear any previous errors

        // Thing to do after ready order, if any
      })
      .addCase(readyOrder.rejected, (state, action) => {
        state.readyOrder.status = 'failed'
        state.readyOrder.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // checkoutOrder
      .addCase(checkoutOrder.pending, (state, action) => {
        state.checkoutOrder.status = 'loading'
      })
      .addCase(checkoutOrder.fulfilled, (state, action) => {
        state.checkoutOrder.status = 'succeeded'
        state.checkoutOrder.error = null // Clear any previous errors

        // Thing to do after checkout order, if any
      })
      .addCase(checkoutOrder.rejected, (state, action) => {
        state.checkoutOrder.status = 'failed'
        state.checkoutOrder.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // addTimeline
      .addCase(addTimeline.pending, (state, action) => {
        state.addTimeline.status = 'loading'
      })
      .addCase(addTimeline.fulfilled, (state, action) => {
        state.addTimeline.status = 'succeeded'
        state.addTimeline.error = null // Clear any previous errors

        // Thing to do after addTimeline, if any
      })
      .addCase(addTimeline.rejected, (state, action) => {
        state.addTimeline.status = 'failed'
        state.addTimeline.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // getTimeline
      .addCase(getTimeline.pending, (state, action) => {
        state.getTimeline.status = 'loading'
      })
      .addCase(getTimeline.fulfilled, (state, action) => {
        state.getTimeline.status = 'succeeded'
        state.getTimeline.error = null // Clear any previous errors

        // Thing to do after getTimeline, if any
      })
      .addCase(getTimeline.rejected, (state, action) => {
        state.getTimeline.status = 'failed'
        state.getTimeline.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // getReview
      .addCase(getReview.pending, (state, action) => {
        state.getReview.status = 'loading'
      })
      .addCase(getReview.fulfilled, (state, action) => {
        state.getReview.status = 'succeeded'
        state.getReview.error = null // Clear any previous errors

        // Thing to do after getReview, if any
      })
      .addCase(getReview.rejected, (state, action) => {
        state.getReview.status = 'failed'
        state.getReview.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }
      })
      // modifyOrderItemPrices
      .addCase(modifyOrderItemPrices.pending, (state, action) => {
        state.modifyOrderItemPrices.status = 'loading'
      })
      .addCase(modifyOrderItemPrices.fulfilled, (state, action) => {
        state.modifyOrderItemPrices.status = 'succeeded'
        state.modifyOrderItemPrices.error = null
      })
      .addCase(modifyOrderItemPrices.rejected, (state, action) => {
        state.modifyOrderItemPrices.status = 'failed'
        state.modifyOrderItemPrices.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      // requestAdditionalPayment
      .addCase(requestAdditionalPayment.pending, (state, action) => {
        state.requestAdditionalPayment.status = 'loading'
      })
      .addCase(requestAdditionalPayment.fulfilled, (state, action) => {
        state.requestAdditionalPayment.status = 'succeeded'
        state.requestAdditionalPayment.error = null
      })
      .addCase(requestAdditionalPayment.rejected, (state, action) => {
        state.requestAdditionalPayment.status = 'failed'
        state.requestAdditionalPayment.error = {
          status: action.payload.status,
          message: action.payload.message,
        }
      })
  },
})
export const { clearStatus } = orderSlice.actions

// Async API (Thunks) - used with useDispatch
export const getPendingOrders = createAsyncThunk(
  'order/getPendingOrders',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const queryParams = new URLSearchParams()
        if (params.page) queryParams.append('page', params.page)
        if (params.limit) queryParams.append('limit', params.limit)

        const queryString = queryParams.toString()
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/pending${
          queryString ? `?${queryString}` : ''
        }`

        const response = await client.get(url)

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getAcceptedOrders = createAsyncThunk(
  'order/getAcceptedOrders',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const queryParams = new URLSearchParams()
        if (params.page) queryParams.append('page', params.page)
        if (params.limit) queryParams.append('limit', params.limit)
        if (params.orderStatus) queryParams.append('orderStatus', params.orderStatus)
        if (params.sortBy) queryParams.append('sortBy', params.sortBy)
        if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection)
        if (params.additionalPaymentStatus) {
          queryParams.append('additionalPaymentStatus', params.additionalPaymentStatus)
        }
        if (params.hasAdditionalChargesOnly) {
          queryParams.append('hasAdditionalChargesOnly', 'true')
        }

        const queryString = queryParams.toString()
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/orders${
          queryString ? `?${queryString}` : ''
        }`

        const response = await client.get(url)

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Tab config for accepted orders: tabIndex -> orderStatus filter (matches Manage.js fetchAcceptedOrders)
const TAB_ORDER_STATUS_FILTERS = {
  2: `${OrderStatus.ASSIGNED_SHOP},${OrderStatus.ASSIGNED_DRIVER},${OrderStatus.ON_PICKUP}`,
  3: String(OrderStatus.SHOP_CHECKIN),
  4: String(OrderStatus.IN_PROGRESS),
  5: `${OrderStatus.READY_FOR_DELIVERY},${OrderStatus.SHOP_CHECKOUT},${OrderStatus.ON_DELIVERY}`,
  6: `${OrderStatus.SHOP_CHECKOUT},${OrderStatus.ON_DELIVERY},${OrderStatus.COMPLETE}`,
}

export const getOrderTabCounts = createAsyncThunk(
  'order/getOrderTabCounts',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (!state.branch?.currentSelectedBranchCode) return { tabCounts: {} }

      const branchCode = state.branch.currentSelectedBranchCode
      const requests = Object.entries(TAB_ORDER_STATUS_FILTERS).map(
        async ([tabIndex, orderStatus]) => {
          const queryParams = new URLSearchParams()
          queryParams.append('page', '1')
          queryParams.append('limit', '1')
          queryParams.append('orderStatus', orderStatus)
          const url = `/shop/branches/${branchCode}/orders?${queryParams.toString()}`
          const res = await client.get(url)
          const total = res?.data?.pagination?.total ?? res?.data?.pagination?.totalPages ?? 0
          return [parseInt(tabIndex, 10), total]
        },
      )

      const tab7Request = async () => {
        const queryParams = new URLSearchParams()
        queryParams.append('page', '1')
        queryParams.append('limit', '1')
        queryParams.append('additionalPaymentStatus', 'Requested')
        const url = `/shop/branches/${branchCode}/orders?${queryParams.toString()}`
        const res = await client.get(url)
        const total = res?.data?.pagination?.total ?? res?.data?.pagination?.totalPages ?? 0
        return [7, total]
      }

      const tabKeys = [...Object.keys(TAB_ORDER_STATUS_FILTERS), '7']
      const results = await Promise.allSettled([...requests, tab7Request()])
      const tabCounts = {}
      results.forEach((result, i) => {
        const tabIndex = tabKeys[i]
        if (result.status === 'fulfilled') {
          tabCounts[result.value[0]] = result.value[1]
        } else {
          tabCounts[parseInt(tabIndex, 10)] = 0
        }
      })
      return { tabCounts }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({ status: 503, message: error.message })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const acceptOrder = createAsyncThunk(
  'order/acceptOrder',
  async (order, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${order.orderNumber}/accept`,
        )

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const checkinOrder = createAsyncThunk(
  'order/checkinOrder',
  async (order, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const payload = {
          ...(order.uploadedTimelinePhotos &&
            Array.isArray(order.uploadedTimelinePhotos) &&
            order.uploadedTimelinePhotos.length && {
              timelinePhotos: order.uploadedTimelinePhotos,
            }),
        }
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${order.orderNumber}/checkin`,
          payload,
        )

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const processOrder = createAsyncThunk(
  'order/processOrder',
  async (order, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const payload = {
          ...(order.uploadedTimelinePhotos &&
            Array.isArray(order.uploadedTimelinePhotos) &&
            order.uploadedTimelinePhotos.length && {
              timelinePhotos: order.uploadedTimelinePhotos,
            }),
        }
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${order.orderNumber}/process`,
          payload,
        )

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const readyOrder = createAsyncThunk(
  'order/readyOrder',
  async (order, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const payload = {
          ...(order.uploadedTimelinePhotos &&
            Array.isArray(order.uploadedTimelinePhotos) &&
            order.uploadedTimelinePhotos.length && {
              timelinePhotos: order.uploadedTimelinePhotos,
            }),
        }
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${order.orderNumber}/ready`,
          payload,
        )

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const checkoutOrder = createAsyncThunk(
  'order/checkoutOrder',
  async (order, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const payload = {
          ...(order.uploadedTimelinePhotos &&
            Array.isArray(order.uploadedTimelinePhotos) &&
            order.uploadedTimelinePhotos.length && {
              timelinePhotos: order.uploadedTimelinePhotos,
            }),
        }
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${order.orderNumber}/checkout`,
          payload,
        )

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const addTimeline = createAsyncThunk(
  'order/addTimeline',
  async (order, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const payload = {
          message: order.timelineMessage,
          ...(order.uploadedTimelinePhotos &&
            Array.isArray(order.uploadedTimelinePhotos) &&
            order.uploadedTimelinePhotos.length && {
              timelinePhotos: order.uploadedTimelinePhotos,
            }),
        }
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${order.orderNumber}/timeline`,
          payload,
        )

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getTimeline = createAsyncThunk(
  'order/getTimeline',
  async (payload, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        let response
        // Get timeline from orderNumber
        if (payload.orderNumber) {
          if (payload.orderNumber.startsWith('ORD')) {
            response = await client.get(
              `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${payload.orderNumber}/timeline`,
            )
          } else if (
            payload.orderNumber.startsWith('TXN') ||
            payload.orderNumber.startsWith('PMT') // PMT for dev backword compatibility
          ) {
            response = await client.get(`/transactions/${payload.orderNumber}/timeline`)
          }
        }

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const unsendTimeline = createAsyncThunk(
  'order/unsendTimeline',
  async ({ orderNumber, timelineId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (!state.branch) {
        return rejectWithValue({ status: 400, message: 'No branch selected' })
      }
      const branchCode = state.branch.currentSelectedBranchCode
      if (orderNumber.startsWith('ORD')) {
        const response = await client.post(
          `/shop/branches/${branchCode}/orders/${orderNumber}/timeline/${timelineId}/unsend`,
        )
        return response
      }
      if (orderNumber.startsWith('TXN') || orderNumber.startsWith('PMT')) {
        const response = await client.post(
          `/transactions/${orderNumber}/timeline/${timelineId}/unsend`,
        )
        return response
      }
      return rejectWithValue({ status: 400, message: 'Invalid order or transaction number' })
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getReview = createAsyncThunk(
  'order/getReview',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      // Access the global state
      const state = getState()
      if (state.branch) {
        const { showAllReviews, needsFix } = params
        const queryParams = new URLSearchParams()

        // If showAllReviews is true, include it in query params
        if (showAllReviews) {
          queryParams.append('showAllReviews', 'true')
        }

        // If needsFix is true, filter for reviews with rating < 3 or hidden
        // This will be handled on frontend, but we still need to get all reviews
        if (needsFix) {
          queryParams.append('showAllReviews', 'true')
        }

        const queryString = queryParams.toString()
        const url = `/shop/branches/${state.branch.currentSelectedBranchCode}/reviews${
          queryString ? `?${queryString}` : ''
        }`

        const response = await client.get(url)

        return response
      }
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const modifyOrderItemPrices = createAsyncThunk(
  'order/modifyOrderItemPrices',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const { orderNumber, modifications } = payload
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${orderNumber}/modify-items`,
          { modifications },
        )
        return response
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const requestAdditionalPayment = createAsyncThunk(
  'order/requestAdditionalPayment',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      if (state.branch) {
        const { orderNumber } = payload
        const response = await client.post(
          `/shop/branches/${state.branch.currentSelectedBranchCode}/orders/${orderNumber}/request-additional-payment`,
        )
        return response
      }
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Selector (Read data) - used with useSelector
export const selectPendingOrders = (state) => state.order.pendingOrders
export const selectPendingOrdersPagination = (state) => state.order.pendingOrdersPagination
export const selectGetPendingOrdersStatus = (state) => state.order.getPendingOrders.status
export const selectGetPendingOrdersError = (state) => state.order.getPendingOrders.error

export const selectAcceptedOrders = (state) => state.order.acceptedOrders
export const selectAcceptedOrdersPagination = (state) => state.order.acceptedOrdersPagination
export const selectOrderTabCounts = (state) => state.order.orderTabCounts
export const selectGetAcceptedOrdersStatus = (state) => state.order.getAcceptedOrders.status
export const selectGetAcceptedOrdersError = (state) => state.order.getAcceptedOrders.error

export const selectAcceptOrderStatus = (state) => state.order.acceptOrder.status
export const selectAcceptOrderError = (state) => state.order.acceptOrder.error

export const selectCheckinOrderStatus = (state) => state.order.checkinOrder.status
export const selectCheckinOrderError = (state) => state.order.checkinOrder.error

export const selectProcessOrderStatus = (state) => state.order.processOrder.status
export const selectProcessOrderError = (state) => state.order.processOrder.error

export const selectReadyOrderStatus = (state) => state.order.readyOrder.status
export const selectReadyOrderError = (state) => state.order.readyOrder.error

export const selectCheckoutOrderStatus = (state) => state.order.checkoutOrder.status
export const selectCheckoutOrderError = (state) => state.order.checkoutOrder.error

export const selectAddTimelineStatus = (state) => state.order.addTimeline.status
export const selectAddTimelineError = (state) => state.order.addTimeline.error

export const selectGetTimelineStatus = (state) => state.order.getTimeline.status
export const selectGetTimelineError = (state) => state.order.getTimeline.error

export const selectGetReviewStatus = (state) => state.order.getReview.status
export const selectGetReviewError = (state) => state.order.getReview.error

export const selectModifyOrderItemPricesStatus = (state) => state.order.modifyOrderItemPrices.status
export const selectModifyOrderItemPricesError = (state) => state.order.modifyOrderItemPrices.error

export const selectRequestAdditionalPaymentStatus = (state) =>
  state.order.requestAdditionalPayment.status
export const selectRequestAdditionalPaymentError = (state) =>
  state.order.requestAdditionalPayment.error

export default orderSlice.reducer
